import { useCallback, useEffect, useState } from "react"

import EventEmitter from "src/EventEmitter"

export type AttendeeInfo = {
  userId: string
  userType: string
  firstName: string | null
  fullName: string
  defaultLiveTranslationLanguageCode: string | null
  liveTranslationEnabled: boolean
  audioPlaybackRate: number
  studentSessionId?: string
}

const useAttendeeInfo = () => {
  const [attendeesInfo, setAttendeesInfo] = useState<
    Record<string, AttendeeInfo>
  >({})

  useEffect(() => {
    const onUserJoined = (
      data: AttendeeInfo & { action: "user_joined_or_updated" }
    ) => {
      const { action: _action, ...info } = data
      setAttendeesInfo(current => ({
        ...current,
        [info.userId]: info,
      }))
    }

    const onUserLeft = (data: { action: "user_left"; userId: string }) => {
      setAttendeesInfo(current => {
        const { [data.userId]: _removed, ...rest } = current
        return rest
      })
    }

    EventEmitter.subscribe("user_joined_or_updated", onUserJoined)
    EventEmitter.subscribe("user_left", onUserLeft)
    return () => {
      EventEmitter.unsubscribe("user_joined_or_updated", onUserJoined)
      EventEmitter.unsubscribe("user_left", onUserLeft)
    }
  }, [])

  const getInfoForExternalUserId = useCallback(
    (externalUserId: string | undefined) => {
      const userId = externalUserId?.split("-")[1]
      if (!userId) return undefined

      return attendeesInfo[userId]
    },
    [attendeesInfo]
  )

  return { attendeesInfo, getInfoForExternalUserId }
}

export default useAttendeeInfo
